import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  noIndex?: boolean;
  /** og:type — defaults to "website", use "article" for blog posts */
  type?: string;
  /** JSON-LD structured data object injected as application/ld+json */
  jsonLd?: Record<string, unknown>;
}

const SEO = ({ title, description, keywords, noIndex, type = "website", jsonLd }: SEOProps) => {
  useEffect(() => {
    document.title = title;

    const setMeta = (selector: string, attr: string, value: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:type"]', "content", type);
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", description);

    if (keywords) {
      setMeta('meta[name="keywords"]', "content", keywords);
    }

    // Self-referencing canonical on the primary domain (avoids duplicate-page reports)
    const canonicalHref = `https://digitalbridgesa.com${window.location.pathname === "/" ? "/" : window.location.pathname.replace(/\/$/, "")}`;
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalHref);
    setMeta('meta[property="og:url"]', "content", canonicalHref);

    // Private pages (dashboard) must never be indexed
    let robots = document.querySelector('meta[name="robots"]');
    if (noIndex) {
      if (!robots) {
        robots = document.createElement("meta");
        robots.setAttribute("name", "robots");
        document.head.appendChild(robots);
      }
      robots.setAttribute("content", "noindex, nofollow");
    } else if (robots?.getAttribute("content")?.includes("noindex")) {
      robots.remove();
    }

    // Page-specific structured data (Article, Service, ...)
    const ldId = "seo-page-jsonld";
    document.getElementById(ldId)?.remove();
    if (jsonLd) {
      const script = document.createElement("script");
      script.id = ldId;
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, noIndex, type, jsonLd]);

  return null;
};

export default SEO;
